import { DynamicListingFeePackagesQuery } from "@/Apollo/schema";
import { ListingFeePackage } from "@/features/create-ad-packages/contracts/create-ad-packages.contracts";

type ListingFeePackageOption =
  DynamicListingFeePackagesQuery["dynamicListingFeePackages"][number];

// The identifier the gateway gives its free Standard row: the ad's own terms, no plan.
const STANDARD_IDENTIFIER = "STANDARD";

// One decimal: a 8.5% commission must not read as 9%.
const toOneDecimal = (value: number) => Math.round(value * 10) / 10;

/**
 * Maps the gateway's plan options into the view model the cards render. Every amount
 * and rate comes from the API as is (WHO-6170): the asking price is `askingPrice`, a
 * plan's rate is its `reducedFeeAmount`, and the rate it is set against is the free
 * Standard row's `reducedFeeAmount` (the ad's own rate). Nothing is worked out from
 * the amounts, so the cards stay right once the payout includes the VAT on the fee.
 */
export const toListingFeePackages = (
  options: readonly ListingFeePackageOption[]
): ListingFeePackage[] => {
  const standardRate = options.find(
    option => option.package.identifier === STANDARD_IDENTIFIER
  )?.package.reducedFeeAmount;

  return options
    .map<ListingFeePackage>(option => {
      const packageCommissionPercent = option.package.reducedFeeAmount;
      const defaultCommissionPercent = standardRate ?? packageCommissionPercent;

      return {
        id: option.package.id,
        title: option.package.title,
        isFree: option.package.price.amount === 0,
        price: option.package.price.amount,
        currency: option.package.price.currency,
        recommended: option.package.isPopular,
        askingPrice: option.askingPrice.amount,
        defaultCommission: option.defaultCommission.amount,
        defaultCommissionPercent,
        packageCommission: option.packageCommission.amount,
        packageCommissionPercent,
        savings: option.savings.amount,
        payout: option.payout.amount,
        feeVat: option.feeVat.amount,
        chargesVatOnFee: option.chargesVatOnFee,
        reductionPercent: Math.max(
          0,
          toOneDecimal(defaultCommissionPercent - packageCommissionPercent)
        ),
      };
    })
    .sort((first, second) => first.price - second.price);
};
